/**
 * SmsTemplates — Admin-CRUD für SMS-Templates + Test-SMS-Versand.
 * Editierbarer Body mit Char-Counter, Variablen-Insert und Live-Vorschau.
 *
 * @page /admin/sms-templates
 * @roles admin, administrator
 * @i18n smsTemplates.*
 * @since v2.415.0 (#947)
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MessageSquare, Pencil, Send, Save, X, RefreshCw, Check, AlertCircle, Plus } from 'lucide-react';
import { Modal } from '../components/ui/Modal';

const API = import.meta.env.VITE_API_URL || '';

const COUNT_GREEN = 160;
const COUNT_YELLOW = 300;

const inp = "w-full px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500";

function renderText(body, vars) {
  if (!body) return '';
  const data = vars || {};
  return body.replace(/\{\{(\w+)\}\}/g, (_m, k) => (data[k] === undefined || data[k] === null ? `[${k}]` : String(data[k])));
}

function countToColor(n) {
  if (n <= COUNT_GREEN) return { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  if (n <= COUNT_YELLOW) return { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' };
}

function smsCountFor(n) {
  if (n <= 160) return 1;
  return Math.ceil(n / 153);
}

function Toast({ message, kind = 'success', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const palette = kind === 'error'
    ? 'bg-rose-50 border-rose-200 text-rose-800'
    : 'bg-emerald-50 border-emerald-200 text-emerald-800';
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-card border shadow-sm text-sm ${palette}`}>
      {message}
    </div>
  );
}

function StatusBadge({ aktiv, t }) {
  return aktiv
    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><Check size={12} />{t('smsTemplates.aktiv')}</span>
    : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--bg-inset)] text-[var(--text-tertiary)] border border-[var(--border-default)]">{t('smsTemplates.deaktiviert')}</span>;
}

function EditModal({ template, onClose, onSaved, showToast }) {
  const { t } = useLanguage();
  const [bezeichnung, setBezeichnung] = useState(template?.bezeichnung || '');
  const [beschreibung, setBeschreibung] = useState(template?.beschreibung || '');
  const [bodyText, setBodyText] = useState(template?.body_text || '');
  const [aktiv, setAktiv] = useState(!!template?.aktiv);
  const [variablen, setVariablen] = useState(Array.isArray(template?.variablen) ? template.variablen : []);
  const [warnLimit, setWarnLimit] = useState(template?.zeichen_warn_limit || 160);
  const [saving, setSaving] = useState(false);
  const [testTelefon, setTestTelefon] = useState('06606153873');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const textareaRef = useRef(null);

  const beispielVars = useMemo(() => {
    const out = {};
    for (const v of variablen) if (v?.key) out[v.key] = v.beispiel || `[${v.key}]`;
    return out;
  }, [variablen]);

  const preview = useMemo(() => renderText(bodyText, beispielVars), [bodyText, beispielVars]);
  const charCount = preview.length;
  const segments = smsCountFor(charCount);
  const counter = countToColor(charCount);

  function insertVar(key) {
    const ta = textareaRef.current;
    if (!ta) {
      setBodyText((prev) => prev + `{{${key}}}`);
      return;
    }
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const next = bodyText.slice(0, start) + `{{${key}}}` + bodyText.slice(end);
    setBodyText(next);
    setTimeout(() => {
      ta.focus();
      const pos = start + key.length + 4;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }

  async function save() {
    if (!bodyText.trim()) {
      showToast(t('smsTemplates.errBodyEmpty'), 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sms-templates/${template.funktion}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bezeichnung,
          beschreibung,
          body_text: bodyText,
          aktiv,
          variablen,
          zeichen_warn_limit: parseInt(warnLimit, 10) || 160,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      showToast(t('smsTemplates.saved'));
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/api/sms-templates/${template.funktion}/test-send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefon: testTelefon, vars: beispielVars }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTestResult({ ok: data.success, msg: data.success ? t('smsTemplates.testSentTo').replace('{n}', data.empfaenger).replace('{c}', data.char_count) : data.error || t('smsTemplates.errGeneric') });
    } catch (err) {
      setTestResult({ ok: false, msg: err.message });
    } finally {
      setTesting(false);
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={`${t('smsTemplates.modalTitle')}: ${bezeichnung || template.funktion}`} size="xl">
      <div className="space-y-4">
        {/* Meta */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('smsTemplates.bezeichnung')}</label>
            <input className={inp} value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('smsTemplates.modul')}</label>
            <input className={`${inp} bg-[var(--bg-inset)]`} value={template.modul || ''} disabled />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('smsTemplates.beschreibung')}</label>
          <input className={inp} value={beschreibung || ''} onChange={(e) => setBeschreibung(e.target.value)} />
        </div>

        {/* Variables Insert */}
        {variablen.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('smsTemplates.varsHint')}</label>
            <div className="flex flex-wrap gap-2">
              {variablen.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVar(v.key)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-default)] bg-[var(--bg-inset)] hover:border-primary-500 hover:bg-white text-[var(--text-secondary)]"
                  title={v.beschreibung || v.key}
                >
                  <Plus size={11} />
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-[var(--text-secondary)]">{t('smsTemplates.body')}</label>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${counter.bg} ${counter.color} ${counter.border}`}>
              {t('smsTemplates.charCount').replace('{c}', charCount).replace('{s}', segments)}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            className={`${inp} font-mono text-sm leading-relaxed`}
            rows={5}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
          />
          <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
            {t('smsTemplates.charLegend')}
          </p>
        </div>

        {/* Preview */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('smsTemplates.preview')}</label>
          <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-inset)] p-3 text-sm whitespace-pre-wrap text-[var(--text-primary)]">
            {preview || <span className="text-[var(--text-tertiary)]">— {t('smsTemplates.empty')} —</span>}
          </div>
        </div>

        {/* Aktiv-Toggle + Warn-Limit */}
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
            {t('smsTemplates.aktivHint')}
          </label>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-[var(--text-secondary)]">{t('smsTemplates.warnLimit')}:</span>
            <input type="number" min={50} max={500} className={`${inp} w-20`} value={warnLimit} onChange={(e) => setWarnLimit(e.target.value)} />
          </div>
        </div>

        {/* Test-Send */}
        <div className="rounded-lg border border-[var(--border-default)] p-3 bg-[var(--bg-inset)]">
          <div className="text-sm font-medium text-[var(--text-primary)] mb-2">{t('smsTemplates.testSend')}</div>
          <div className="flex items-center gap-2">
            <input
              className={`${inp} max-w-[220px]`}
              value={testTelefon}
              onChange={(e) => setTestTelefon(e.target.value)}
              placeholder="06606153873"
            />
            <button
              type="button"
              onClick={sendTest}
              disabled={testing || !bodyText.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            >
              {testing ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
              {t('smsTemplates.testBtn')}
            </button>
          </div>
          {testResult && (
            <div className={`mt-2 text-xs ${testResult.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
              {testResult.ok ? '✓ ' : '✗ '}{testResult.msg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border-default)]">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
            {t('smsTemplates.cancel')}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            {t('smsTemplates.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function SmsTemplates() {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [filterModul, setFilterModul] = useState('alle');

  function showToast(message, kind = 'success') {
    setToast({ message, kind });
  }

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sms-templates`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const moduleList = useMemo(() => {
    const set = new Set(templates.map((t) => t.modul).filter(Boolean));
    return ['alle', ...Array.from(set).sort()];
  }, [templates]);

  const filtered = useMemo(() => {
    if (filterModul === 'alle') return templates;
    return templates.filter((t) => t.modul === filterModul);
  }, [templates, filterModul]);

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#338edc]/10 flex items-center justify-center">
          <MessageSquare size={20} className="text-[#338edc]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('smsTemplates.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary)]">{t('smsTemplates.subtitle')}</p>
        </div>
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
        >
          <RefreshCw size={14} />
          {t('smsTemplates.reload')}
        </button>
      </div>

      {/* Modul-Filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        {moduleList.map((m) => (
          <button
            key={m}
            onClick={() => setFilterModul(m)}
            className={`px-3 py-1.5 text-xs rounded-full border ${
              filterModul === m
                ? 'bg-[#338edc] text-white border-[#338edc]'
                : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] border-[var(--border-default)] hover:bg-white'
            }`}
          >
            {m === 'alle' ? t('smsTemplates.all') : m}
          </button>
        ))}
      </div>

      {/* Liste */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">{t('smsTemplates.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-tertiary)]">{t('smsTemplates.empty_list')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset)] text-left text-xs uppercase tracking-wide text-[var(--text-tertiary)]">
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colFunktion')}</th>
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colBezeichnung')}</th>
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colModul')}</th>
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colPreview')}</th>
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colStatus')}</th>
                <th className="px-4 py-3 font-medium">{t('smsTemplates.colAction')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default)]">
              {filtered.map((tpl) => {
                const charCount = (tpl.body_text || '').length;
                const counter = countToColor(charCount);
                return (
                  <tr key={tpl.funktion} className="hover:bg-[var(--bg-inset)]/40">
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">{tpl.funktion}</td>
                    <td className="px-4 py-3 text-[var(--text-primary)]">
                      <div className="font-medium">{tpl.bezeichnung}</div>
                      {tpl.beschreibung && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{tpl.beschreibung}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {tpl.modul && <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg-inset)] border border-[var(--border-default)] text-[var(--text-secondary)]">{tpl.modul}</span>}
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-xs text-[var(--text-tertiary)] truncate" title={tpl.body_text}>
                        {tpl.body_text}
                      </div>
                      <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border ${counter.bg} ${counter.color} ${counter.border}`}>
                        {charCount} Z.
                      </span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge aktiv={tpl.aktiv} t={t} /></td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setEditing(tpl)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-primary-500 hover:text-primary-600 hover:bg-white"
                      >
                        <Pencil size={12} />
                        {t('smsTemplates.edit')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Hinweis */}
      <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
        <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
        <div>{t('smsTemplates.cacheHint')}</div>
      </div>

      {editing && (
        <EditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          showToast={showToast}
        />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
    </div>
  );
}
